import { useEffect, useRef, useState } from 'react'
import { Field, Form, Formik } from 'formik'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { sileo } from 'sileo'
import { useGlobalContext } from '../../../../../context/GlobalState'
import UserApi from '../../../../../api/user.api'
import {
  ensureRecaptcha,
  executeRecaptcha,
  isRecaptchaConfigured,
  setRecaptchaSiteKey
} from '../../../../../utils/recaptcha'

const SUPPORT_RECAPTCHA_SITE_KEY = (() => {
  const DEV_KEY = '6LfYbeUrAAAAAMhrKIhziXwgR41vf9kCHRk68WAd'
  const PROD_KEY = '6LdZ3hksAAAAAH72xU5vxjiyIhgAZCQGDInspi84'
  if (typeof window === 'undefined') return DEV_KEY
  const host = window.location.hostname.toLowerCase()
  const isDevHost = host === 'localhost' || host === '127.0.0.1' || host.includes('dev')
  return isDevHost ? DEV_KEY : PROD_KEY
})()

const MIN_SECONDS = 4
const COOLDOWN_MS = 15000

const extractErrorMessage = error => {
  if (!error) return 'Ocurrió un error al enviar el mensaje'

  const status = error?.response?.status
  if (status === 503) return 'Soporte temporalmente no disponible. Inténtalo más tarde.'

  const payload =
    error?.response?.data?.reason || error?.response?.data?.message || error?.response?.data?.error

  if (typeof payload === 'string' && payload.includes('INVALID_REASON')) {
    return 'No pudimos validar seguridad (reCAPTCHA). Recarga la página e inténtalo nuevamente.'
  }

  if (Array.isArray(payload)) return payload.join(', ')
  if (payload) return payload
  if (error instanceof Error && error.message) return error.message
  return 'Ocurrió un error al enviar el mensaje'
}

const ContactSupport = () => {
  const { currentBusiness } = useGlobalContext()
  const [startTs, setStartTs] = useState(Date.now())
  const cooldownUntilRef = useRef(0)
  const [userInfo, setUserInfo] = useState(null)
  const [loadingUser, setLoadingUser] = useState(true)

  useEffect(() => {
    setStartTs(Date.now())
  }, [])

  useEffect(() => {
    let active = true
    setLoadingUser(true)
    UserApi.getMe()
      .then(({ data }) => {
        if (active) setUserInfo(data)
      })
      .catch(error => {
        console.error('Error al obtener usuario actual', error)
      })
      .finally(() => {
        if (active) setLoadingUser(false)
      })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!isRecaptchaConfigured()) {
      setRecaptchaSiteKey(SUPPORT_RECAPTCHA_SITE_KEY)
    }
  }, [])

  useEffect(() => {
    if (!isRecaptchaConfigured()) {
      console.warn('reCAPTCHA site key not configured. Support form will be disabled.')
      return
    }

    ensureRecaptcha().catch(error => {
      console.error('No se pudo inicializar reCAPTCHA Enterprise', error)
    })
  }, [])

  const resolveBusinessId = () =>
    userInfo?.data?.find?.(item => item?.type === 'business')?.id ?? currentBusiness?.id ?? null

  const resolveOrganizationId = () =>
    currentBusiness?.organizationId ?? userInfo?.currentOrganizationId ?? null

  const resolveReplyTo = () => {
    const email = userInfo?.email || currentBusiness?.email || ''
    const trimmed = email.trim()
    return trimmed.length ? trimmed : undefined
  }

  const resolveOrgId = () =>
    currentBusiness?.organizationId ?? userInfo?.currentOrganizationId ?? undefined

  return (
    <div className='flex flex-col gap-6'>
      <Formik
        initialValues={{ message: '', _honey: '' }}
        enableReinitialize={false}
        onSubmit={async (values, actions) => {
          try {
            if (values._honey && values._honey.trim()) return

            const elapsed = (Date.now() - startTs) / 1000
            if (elapsed < MIN_SECONDS) {
              sileo.error({
                title: 'Momento...',
                description: (
                  <span className='text-red-500/80 font-medium'>
                    Espera un momento antes de enviar.
                  </span>
                )
              })
              return
            }

            if (Date.now() < cooldownUntilRef.current) {
              sileo.error({
                title: 'Cooldown Activo',
                description: (
                  <span className='text-red-500/80 font-sans font-medium'>
                    Espera antes de volver a enviar.
                  </span>
                )
              })
              return
            }

            const businessId = resolveBusinessId()
            if (!businessId) {
              sileo.error({
                title: 'Información Faltante',
                description: (
                  <span className='text-red-500/80 font-sans font-medium'>
                    {loadingUser
                      ? 'Obteniendo la información de tu empresa... intenta nuevamente en unos segundos.'
                      : 'No pudimos identificar la organización seleccionada.'}
                  </span>
                )
              })
              return
            }

            const organizationId = resolveOrganizationId()
            if (!organizationId) {
              sileo.error({
                title: 'Información Faltante',
                description: (
                  <span className='text-red-500/80 font-sans font-medium'>
                    {loadingUser
                      ? 'Obteniendo la información de tu empresa... intenta nuevamente en unos segundos.'
                      : 'No pudimos identificar la organización seleccionada.'}
                  </span>
                )
              })
              return
            }

            const message = (values.message || '').trim()
            if (message.length < 10) {
              sileo.error({
                title: 'Mensaje Corto',
                description: (
                  <span className='text-red-500/80 font-sans font-medium'>
                    El mensaje debe tener al menos 10 caracteres.
                  </span>
                )
              })
              return
            }

            if (!isRecaptchaConfigured()) {
              sileo.error({
                title: 'Error de Seguridad',
                description: (
                  <span className='text-red-500/80 font-sans font-medium'>
                    La validación de seguridad no está disponible. Contacta al administrador.
                  </span>
                )
              })
              return
            }

            let captchaToken
            try {
              captchaToken = await executeRecaptcha('support_contact')
              console.debug('[support/contact] captcha token acquired', {
                hasToken: Boolean(captchaToken),
                tokenLength: captchaToken?.length ?? 0,
                preview: captchaToken?.slice(0, 40) ?? null
              })
            } catch (captchaError) {
              console.error('No se pudo obtener el token de reCAPTCHA', captchaError)
              sileo.error({
                title: 'Validación Fallida',
                description: (
                  <span className='text-red-500/80 font-sans font-medium'>
                    No pudimos verificar que eres humano. Refresca la página e inténtalo nuevamente.
                  </span>
                )
              })
              return
            }

            const payload = {
              message,
              replyTo: resolveReplyTo(),
              orgId: resolveOrgId(),
              businessId,
              captchaToken
            }

            console.debug('[support/contact] payload preview', {
              ...payload,
              captchaToken: `${captchaToken.slice(0, 40)}...`
            })

            await sileo.promise(UserApi.contactSupport(organizationId, payload), {
              loading: { title: 'Soporte', description: 'Enviando mensaje...' },
              success: {
                title: 'Mensaje Enviado',
                description: (
                  <span className='text-green-500/80 font-sans font-medium'>Mensaje enviado con éxito</span>
                )
              },
              error: {
                title: 'Error de Soporte',
                description: 'No se pudo enviar el mensaje'
              }
            })

            cooldownUntilRef.current = Date.now() + COOLDOWN_MS
          } catch (error) {
            sileo.error({
              title: 'Error Inesperado',
              description: (
                <span className='text-red-500/80 font-sans font-medium'>{extractErrorMessage(error)}</span>
              )
            })
          } finally {
            actions.setSubmitting(false)
            actions.resetForm()
            setStartTs(Date.now())
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form className='w-full h-full p-8'>
            <ul className='w-full flex flex-col gap-3'>
              <li className='flex gap-2 items-center'>
                <InfoOutlinedIcon fontSize='large' />
                <h4 className='font-sans font-semibold'>
                  ¿Necesitas ayuda, tienes dudas o quieres contarnos algo?
                </h4>
              </li>
              <p className='text-sm font-sans'>
                Escríbenos mediante este formulario y nuestro equipo de soporte recibirá tu mensaje.
              </p>

              <div
                style={{ position: 'absolute', left: '-10000px', height: 0, overflow: 'hidden' }}
                aria-hidden
              >
                <label className='font-sans'>
                  Si eres humano, deja este campo vacío:
                  <Field name='_honey' autoComplete='off' tabIndex={-1} />
                </label>
              </div>

              <Field
                className='w-full rounded-lg'
                as='textarea'
                placeholder='Detalla aquí los problemas...'
                name='message'
                minLength={10}
                required
              />

              <button
                type='submit'
                disabled={isSubmitting}
                className='rounded-full ml-auto w-fit btn-light text-black py-1 font-extrabold text-sm disabled:opacity-50'
              >
                Enviar
              </button>
            </ul>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default ContactSupport
